import { Box, SxProps, Theme } from "@outschool/backpack";
import React, { CSSProperties } from "react";

import EnglishOnly from "./EnglishOnly";

/**
 *
 * Making trustpilot widgets look like what you want is very tricky so
 * expect some friction.
 *
 */

/**
 *
 * Find new trustpilot templates in the website:
 *
 * https://businessapp.b2b.trustpilot.com/trustbox/
 *
 */
export enum TemplateId {
  Horizontal = "5406e65db0d04a09e042d5fc",
  MicroCombo = "5419b6ffb0d04a076446a9af",
  Slider = "54ad5defc6454f065c28af8b",
  Starter = "5613c9cde69ddc09340c6beb",
  MicroTrustScore = "5419b637fa0340045cd0c936",
  Mini = "53aa8807dec7e10d38f59f32",
}

/**
 *
 * Build the src URL for the widget served via Outschool route /trustpilot-widget/
 *
 * The problem that serving through our own iframe solve is that when react
 * updates the rendering tree, your widgets re-renders automatically.
 *
 * @param {string} templateId
 * @param {Record} params
 */
const makeSrc = (
  host: string,
  templateId: string,
  params: Record<string, string>
) => `${host}/trustpilot-widget/${templateId}${objectToQueryParams(params)}`;

/**
 *
 * Trustpilot widgets are served through the main app and this function
 * returns the right url prefix to hit the main app from any environment.
 *
 */
export const getWidgetUrlPrefix = ({
  isSSR,
  env,
  appHostname,
}: {
  isSSR: boolean;
  env: string;
  appHostname: string;
}): string => {
  if (!isSSR) {
    return "";
  }

  if (env === "development") {
    return "http://localhost:3000";
  }

  if (env === "production") {
    return "https://outschool.com";
  }

  return "https://" + appHostname.replace("server-rendered-website", "website");
};

/**
 *
 * Simple util to convert an object into a query string
 *
 * {a: 1} => "?a=1"
 * {a: 1, b: 2} => "?a=1&b=2"
 *
 */
const objectToQueryParams = (obj: Record<string, string>) =>
  "?" + new URLSearchParams(obj).toString();

/**
 *
 * Data parameters passed into the TP widget in the form of data-*.
 * This is a base and each widget has different options.
 *
 * More info: https://businessapp.b2b.trustpilot.com/trustbox/5419b6ffb0d04a076446a9af
 * You need an account.
 */
const WIDGET_DEFAULT_DATA_PARAMS = {
  locale: "en-US",
  theme: "light",
};

/**
 * Base SX configuration for our outer iframe that contains the Trustpilot widget iframe.
 *
 */
const OS_IFRAME_SX = {
  border: "none",
};
/**
 *
 * Styling params passed to the inner Trustpilot iframe in the form of data-style-*
 *
 * Find more specific info for your widget in the trustpilot page.
 *
 */
const TRUSTPILOT_IFRAME_SX: CSSProperties = {
  width: "100%",
};

/**
 *
 * Prefixes all keys with "data-style-".
 */
const makeWidgetStyleParams = (params = {}) =>
  Object.entries({ ...TRUSTPILOT_IFRAME_SX, ...params }).reduce(
    (acc, [k, v]) => ({ ...acc, ["style-" + k]: v }),
    {}
  );

interface ComponentArgs {
  /**
   *
   * Theme-aware style applied to the containing iframe
   */
  sx?: SxProps;

  /**
   *
   * Non-theme aware style passed onto the trustpilot widget in the form
   * data-style-*
   */
  widgetCss?: CSSProperties;

  /**
   *
   * Configuration passed into the widget in the form of data-*
   */
  widgetData?: Record<string, string>;

  /**
   *
   *
   */
  urlPrefix?: string;

  /**
   *
   * MicroCombo is center aligned by default
   * if leftAlignedNoWrap = true, you'll get a non-wrapped left aligned version
   */
  leftAlignedNoWrap?: boolean;
}

enum MicroComboDefaultConfig {
  height = "48px",
}

const MICROCOMBO_NOWRAP_WIDTH = 420;
const MICROCOMBO_NOWRAP_HEIGHT = 24;
const OS_MOBILE_MARGIN = 14;

export function TrustpilotMicroCombo({
  sx = {},
  widgetCss = {},
  widgetData = {},
  urlPrefix = "",
  leftAlignedNoWrap = false,
}: ComponentArgs) {
  return (
    <EnglishOnlyWidget
      src={makeSrc(urlPrefix, TemplateId.MicroCombo, {
        ...WIDGET_DEFAULT_DATA_PARAMS,
        ...makeWidgetStyleParams(widgetCss),
        ...widgetData,
      })}
      sx={
        leftAlignedNoWrap
          ? [
              (theme: Theme) => ({
                [theme.breakpoints.down(
                  MICROCOMBO_NOWRAP_WIDTH + 2 * OS_MOBILE_MARGIN
                )]: {
                  transform: "scale(0.8)",
                  transformOrigin: "left",
                },
              }),
              {
                height: MICROCOMBO_NOWRAP_HEIGHT,
                minWidth: MICROCOMBO_NOWRAP_WIDTH,
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]
          : [
              (theme: Theme) => ({
                [theme.breakpoints.down("md")]: {
                  marginLeft: "-20%",
                  transform: "scale(0.8)",
                  width: "135%",
                },
                [theme.breakpoints.up("md")]: {
                  marginLeft: "-13px",
                  width: "436px",
                },
              }),
              {
                height: widgetCss.height ?? MicroComboDefaultConfig.height,
              },
              ...(Array.isArray(sx) ? sx : [sx]),
            ]
      }
    />
  );
}

export function TrustpilotSlider({
  sx = {},
  widgetCss = {},
  widgetData = {},
  urlPrefix = "",
}: ComponentArgs) {
  const osIframeSx = [{ width: "100%" }, ...(Array.isArray(sx) ? sx : [sx])];
  return (
    <EnglishOnlyWidget
      src={makeSrc(urlPrefix, TemplateId.Slider, {
        ...WIDGET_DEFAULT_DATA_PARAMS,
        ...makeWidgetStyleParams(widgetCss),
        ...widgetData,
      })}
      sx={osIframeSx}
    />
  );
}

export function TrustpilotMini({
  sx = {},
  widgetCss = {},
  widgetData = {},
  urlPrefix = "",
}: ComponentArgs) {
  const osIframeSx = [{ width: "100%" }, ...(Array.isArray(sx) ? sx : [sx])];
  return (
    <EnglishOnlyWidget
      src={makeSrc(urlPrefix, TemplateId.Mini, {
        ...WIDGET_DEFAULT_DATA_PARAMS,
        ...makeWidgetStyleParams(widgetCss),
        ...widgetData,
      })}
      sx={osIframeSx}
    />
  );
}

export function TrustpilotStarter({
  sx = {},
  widgetCss = {},
  widgetData = {},
  urlPrefix = "",
}: ComponentArgs) {
  return (
    <EnglishOnlyWidget
      src={makeSrc(urlPrefix, TemplateId.Starter, {
        ...WIDGET_DEFAULT_DATA_PARAMS,
        ...makeWidgetStyleParams({
          ...{
            width: "100%",
            height: "100%",
          },
          ...widgetCss,
        }),
        ...widgetData,
      })}
      sx={Array.isArray(sx) ? sx : [sx]}
    />
  );
}

export function TrustpilotHorizontal({
  sx = {},
  widgetCss = {},
  widgetData = {},
  urlPrefix = "",
}: ComponentArgs) {
  return (
    <EnglishOnlyWidget
      src={makeSrc(urlPrefix, TemplateId.Horizontal, {
        ...WIDGET_DEFAULT_DATA_PARAMS,
        ...makeWidgetStyleParams({
          ...widgetCss,
        }),
        ...widgetData,
      })}
      sx={[
        { width: "100%", height: "24px" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

export function TrustpilotMicroTrustScore({
  sx = {},
  widgetCss = {},
  widgetData = {},
  urlPrefix = "",
}: ComponentArgs) {
  return (
    <EnglishOnlyWidget
      src={makeSrc(urlPrefix, TemplateId.MicroTrustScore, {
        ...WIDGET_DEFAULT_DATA_PARAMS,
        ...makeWidgetStyleParams({
          ...widgetCss,
        }),
        ...widgetData,
      })}
      sx={[{ height: "20px" }, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
}

/**
 * Trustpilot renders for English only
 *
 */
function EnglishOnlyWidget({ ...props }) {
  return (
    <EnglishOnly>
      {/* eslint-disable i18next/no-literal-string */}
      <Box
        as="iframe"
        title="Trustpilot reviews"
        scrolling="no"
        loading="lazy"
        {...props}
        sx={[OS_IFRAME_SX, ...props.sx]}
      />
    </EnglishOnly>
  );
}
